import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Buildings, Plus, Lock, Image as ImageIcon, UploadSimple, Trash, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Municipalities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ibge_code: "", name: "", region: "" });
  const [identityMuni, setIdentityMuni] = useState(null);

  const load = async () => {
    const { data } = await api.get("/municipalities");
    setItems(data);
    const s = await api.get("/dashboard/stats");
    const map = {};
    s.data.por_municipio.forEach((m) => { map[m.nome] = m; });
    setStats(map);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/municipalities", form);
      toast.success("Município cadastrado");
      setForm({ ibge_code: "", name: "", region: "" });
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao cadastrar";
      toast.error(typeof msg === "string" ? msg : "Erro ao cadastrar");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="uppercase-label">Cadastro base</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Municípios</h1>
          <p className="text-sm text-[#94A3B8] mt-2">{items.length} municípios do Estado do Ceará</p>
        </div>
        {isAdmin ? (
          <button
            data-testid="toggle-add-municipality"
            onClick={() => setShowForm((s) => !s)}
            className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium hover:bg-[#FCD34D] flex items-center gap-2 transition-colors"
          >
            <Plus size={16} weight="bold" /> Novo município
          </button>
        ) : (
          <div
            data-testid="admin-only-badge"
            title="Apenas o Administrador Geral pode cadastrar novos municípios"
            className="px-3 py-2 rounded-md border border-[#334155] text-[#94A3B8] text-xs flex items-center gap-2"
          >
            <Lock size={14} weight="duotone" /> Cadastro restrito ao Administrador Geral
          </div>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={create} className="tce-card p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="uppercase-label block mb-2">Código IBGE</label>
            <input data-testid="ibge-input" required value={form.ibge_code}
                   onChange={(e) => setForm({ ...form, ibge_code: e.target.value })}
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm" placeholder="Ex: 2304400" />
          </div>
          <div>
            <label className="uppercase-label block mb-2">Nome</label>
            <input data-testid="muni-name-input" required value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm" placeholder="Ex: Fortaleza" />
          </div>
          <div>
            <label className="uppercase-label block mb-2">Região</label>
            <input data-testid="region-input" value={form.region}
                   onChange={(e) => setForm({ ...form, region: e.target.value })}
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm" placeholder="Ex: Metropolitana" />
          </div>
          <button data-testid="save-municipality"
                  className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium hover:bg-[#FCD34D] transition-colors">
            Salvar
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((m) => {
          const s = stats[m.name];
          const hasLogo = !!m.logo_base64 || !!m.brasao_base64;
          const logoUrl = `${API}/municipalities/${m.ibge_code}/imagem/${m.logo_base64 ? "logo" : "brasao"}?v=${m.logo_updated_at || m.brasao_updated_at || ""}`;
          return (
            <div key={m.ibge_code} className="tce-card p-6 relative group" data-testid={`muni-card-${m.ibge_code}`}>
              <Link to={`/contratos?municipio_id=${m.ibge_code}`} className="block">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="uppercase-label">{m.region || "—"}</div>
                    <div className="font-heading text-2xl mt-2 group-hover:text-[#FFDF00] transition-colors">{m.name}</div>
                    <div className="text-xs text-[#94A3B8] mt-1 font-mono">IBGE {m.ibge_code}</div>
                  </div>
                  {hasLogo ? (
                    <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden border border-[#334155]">
                      <img src={logoUrl} alt={m.name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-[#1E293B] flex items-center justify-center text-[#FFDF00] border border-[#334155]">
                      <Buildings size={22} weight="duotone" />
                    </div>
                  )}
                </div>
                {s && (
                  <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-[#334155]">
                    <div>
                      <div className="uppercase-label">Contratos</div>
                      <div className="font-heading text-xl mt-1">{s.count}</div>
                    </div>
                    <div>
                      <div className="uppercase-label">Valor total</div>
                      <div className="font-heading text-xl mt-1 gold-text">
                        R$ {(s.valor / 1000000).toFixed(2)}M
                      </div>
                    </div>
                  </div>
                )}
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdentityMuni(m); }}
                data-testid={`muni-identity-${m.ibge_code}`}
                title="Gerenciar Logo e Brasão institucional"
                className="mt-4 w-full px-3 py-2 rounded-md border border-[#FFDF00]/40 text-[#FFDF00] hover:bg-[#FFDF00]/10 text-xs font-medium flex items-center justify-center gap-2"
              >
                <ImageIcon size={14} weight="duotone" />
                {hasLogo ? "Editar Identidade Visual" : "Enviar Logo/Brasão"}
              </button>
            </div>
          );
        })}
      </div>

      {identityMuni && (
        <MunicipalityIdentityModal
          muni={identityMuni}
          onClose={() => setIdentityMuni(null)}
          onSaved={() => { setIdentityMuni(null); load(); }}
        />
      )}
    </div>
  );
}


/* ============================================================
 * MunicipalityIdentityModal — Upload de Logo e Brasão oficial
 * ============================================================ */
function MunicipalityIdentityModal({ muni, onClose, onSaved }) {
  const [busy, setBusy] = useState(null);
  const [versions, setVersions] = useState({ logo: Date.now(), brasao: Date.now() });
  const [hasLogo, setHasLogo] = useState(!!muni.logo_base64);
  const [hasBrasao, setHasBrasao] = useState(!!muni.brasao_base64);

  const upload = async (tipo, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Arquivo maior que 5MB"); return; }
    setBusy(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/municipalities/${muni.ibge_code}/imagem?tipo=${tipo}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${tipo === "logo" ? "Logo" : "Brasão"} enviado (${r.data.size_kb}KB)`);
      if (tipo === "logo") setHasLogo(true); else setHasBrasao(true);
      setVersions((v) => ({ ...v, [tipo]: Date.now() }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally { setBusy(null); }
  };

  const remove = async (tipo) => {
    if (!window.confirm(`Remover ${tipo === "logo" ? "logo" : "brasão"} atual?`)) return;
    setBusy(tipo);
    try {
      await api.delete(`/municipalities/${muni.ibge_code}/imagem/${tipo}`);
      if (tipo === "logo") setHasLogo(false); else setHasBrasao(false);
      setVersions((v) => ({ ...v, [tipo]: Date.now() }));
      toast.success("Removido");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao remover"); }
    finally { setBusy(null); }
  };

  const Slot = ({ tipo, label, hint, hasImg }) => {
    const url = `${API}/municipalities/${muni.ibge_code}/imagem/${tipo}?v=${versions[tipo]}`;
    return (
      <div className="border border-[#334155] rounded-md p-5 bg-[#0F172A]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading text-lg">{label}</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">{hint}</div>
          </div>
          {hasImg && (
            <button onClick={() => remove(tipo)} disabled={busy}
                    className="p-1.5 rounded hover:bg-red-500/20 text-red-400" title="Remover"
                    data-testid={`identity-remove-${tipo}`}>
              <Trash size={16} />
            </button>
          )}
        </div>
        <div className="flex flex-col items-center gap-4">
          <div className="w-40 h-40 rounded-md bg-white flex items-center justify-center overflow-hidden border-2 border-dashed border-[#334155]">
            {hasImg ? (
              <img src={url} alt={label} className="w-full h-full object-contain" data-testid={`identity-preview-${tipo}`} />
            ) : (
              <div className="text-[#94A3B8] text-xs text-center px-4">Sem {label.toLowerCase()} cadastrado</div>
            )}
          </div>
          <label className="w-full">
            <input type="file" accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    disabled={busy === tipo}
                    onChange={(e) => upload(tipo, e.target.files?.[0])}
                    data-testid={`identity-file-${tipo}`} />
            <div className={`w-full px-3 py-2 rounded-md border cursor-pointer text-xs font-medium text-center flex items-center justify-center gap-2 ${
              busy === tipo
                ? "border-[#334155] text-[#64748B] cursor-wait"
                : "border-[#FFDF00]/40 text-[#FFDF00] hover:bg-[#FFDF00]/10"
            }`}>
              <UploadSimple size={13} weight="bold" />
              {busy === tipo ? "Enviando…" : hasImg ? `Substituir ${label.toLowerCase()}` : `Enviar ${label.toLowerCase()}`}
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between gap-3">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <ImageIcon size={12} weight="duotone" className="text-[#FFDF00]" /> Identidade visual do município
            </div>
            <h2 className="font-heading text-2xl mt-1">{muni.name} <span className="text-[#94A3B8] font-normal text-lg">/ CE</span></h2>
            <p className="text-xs text-[#94A3B8] mt-1">
              PNG, JPG ou WEBP · até 5MB · redimensionados automaticamente para 400px.
              Aplicados na capa e no cabeçalho do <b>PDF Apresentação PCA</b>.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded" data-testid="identity-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Slot tipo="brasao" label="Brasão" hint="Exibido em destaque na capa (grande, quadrado)" hasImg={hasBrasao} />
          <Slot tipo="logo" label="Logo" hint="Exibida no cabeçalho de todas as páginas (pequena)" hasImg={hasLogo} />
        </div>

        <div className="p-4 border-t border-[#334155] flex justify-between items-center">
          <div className="text-xs text-[#94A3B8]">Dica: use imagens quadradas com fundo transparente (PNG) para melhor resultado.</div>
          <button onClick={onSaved} className="px-5 py-2 rounded-md bg-[#009C3B] text-white font-medium text-sm flex items-center gap-2"
                  data-testid="identity-done">
            <CheckCircle size={14} weight="bold" /> Concluído
          </button>
        </div>
      </div>
    </div>
  );
}
